/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { Dialog, makeStyles, Typography } from '@material-ui/core';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import WarningIcon from '@material-ui/icons/Warning';
import CheckIcon from '@material-ui/icons/Check';
import ClearIcon from '@material-ui/icons/Clear';
import AlertDialog from '../common/alertDialog/AlertDialog';
import DuplicateImportContactDialog from './duplicate/DuplicateImportContactDialog';
import Button from '../common/Button';
import { ImportStatus } from './importStatus';
import useImportContactDialog from './useImportContactDialog';
import DropZone from '../dropzone/DropZone';

const useStyles = makeStyles(theme => ({
  dropZone: {
    display: 'flex',
    justifyContent: 'center',
    padding: '2rem 15rem',
  },
  dropZoneParagraph: {
    fontSize: `${theme.typography.htmlFontSize}px !important`,
    color: '#677289',
  },
  buttons: {
    display: 'flex',
    flexFlow: 'row wrap',
    justifyContent: 'flex-end',
    margin: '1rem',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'space-evenly',
    },
  },
  importButton: {
    margin: '0.5rem',
    padding: '0.5rem 2rem',
    backgroundColor: '#066AE3',
    color: '#FFF',
  },
  cancelButton: {
    margin: '0.5rem',
    padding: '0.5rem 2rem',
    backgroundColor: '#D9DDE3',
    color: '#21252C',
  },
  error: {
    margin: '1em',
    color: '#CE4722',
  },
  checkIcon: {
    color: '#066AE3',
  },
  clearIcon: {
    float: 'right',
    cursor: 'pointer',
  },
  uploaded: {
    margin: '1em',
  },
}));

export default function ImportContactDialog(props) {
  const classes = useStyles();
  const { open, onCancel, loadData } = props;
  const {
    file,
    errorMessage,
    importResult,
    successfulImport,
    onChange,
    onClose,
    onDropRejected,
    onSend,
    onClear,
    onAllDuplicatesResolved,
    onCancelImport,
    t,
  } = useImportContactDialog(onCancel, loadData);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Typography component="span" variant="h2">
          {t('import.contact')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <div className={classes.title}>{t('messages.drag.files.contacts.import')}</div>
        <DropZone
          id="contact-dropzone"
          filesLimit={1}
          showPreviews={false}
          acceptedMimeTypes={['.csv', 'text/csv']}
          onDrop={onChange}
          disabled={!!file}
          onDropRejected={onDropRejected}
        />
        {errorMessage && (
          <div className={classes.error}>
            <WarningIcon fontSize="small" viewBox="0 0 22 20" /> 
            {' '}
            {errorMessage}
          </div>
        )}
        {file && (
          <div className={classes.uploaded}>
            <CheckIcon className={classes.checkIcon} fontSize="small" viewBox="0 0 22 15" /> 
            {' '}
            {file.name}
            <ClearIcon
              className={classes.clearIcon}
              fontSize="small"
              viewBox="0 0 22 15"
              onClick={onClear}
              data-testid="clearIcon"
            />
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="default" onClick={onClose}>
          {t('actions.cancel')}
        </Button>
        <Button id="contact-dropzone-confirmUpload" color="primary" onClick={onSend} disabled={!file}>
          {t('Import')}
        </Button>
      </DialogActions>
      {importResult?.status === ImportStatus.MERGING && (
        <DuplicateImportContactDialog
          importResult={importResult}
          onAllDuplicatesResolved={onAllDuplicatesResolved}
          onCancelImport={onCancelImport}
        />
      )}
      <AlertDialog
        isOpen={!!successfulImport}
        title="import.contact"
        message="messages.contacts.import.success"
        confirmButtonMessage="actions.ok"
        handleConfirmButton={onClose}
      />
    </Dialog>
  );
}
